import { type Quantity } from '@luminovo/api-contracts';
import {
    compareByDate,
    compareByNumber,
    compareByString,
    extractAmountFromMonetaryValue,
    isEqual,
    isPresent,
    logToExternalErrorHandlers,
    MonetaryValue,
    QuantityHelper,
    throwErrorUnlessProduction,
} from '@luminovo/commons';
import { colorSystem } from '@luminovo/design-system';
import { rankings, rankItem } from '@tanstack/match-sorter-utils';

import {
    Column,
    ColumnDef,
    FilterFn,
    RegistryFilterFn,
    RegistrySortFn,
    RowData,
    SortFn,
    Table,
} from '../tanstackTypes';
import {
    DateRange,
    FilterValue,
    NumberRange,
    RelativeDateFilterValue,
    SupportedFilterFn,
    UseTanStackTableStateProps,
} from '../type';

function flattenColumnDefs<TData extends RowData>(columns: ColumnDef<TData, any>[]): ColumnDef<TData, any>[] {
    const result: ColumnDef<TData, any>[] = [];
    for (const col of columns) {
        const children = (col as any).columns as ColumnDef<TData, any>[] | undefined;
        if (children && children.length > 0) {
            result.push(...flattenColumnDefs(children));
        } else {
            result.push(col);
        }
    }
    return result;
}

export function createFuzzyFilter<TData extends RowData>(columns: ColumnDef<TData, any>[]): FilterFn<TData> {
    const leafColumns = flattenColumnDefs(columns);
    return (row, columnId, filterValue) => {
        const columnDef = leafColumns.find((col) => col.id === columnId);

        if (!columnDef) {
            throwErrorUnlessProduction(`Column with id ${columnId} not found in the columns array.`);
            return false;
        }

        const rawValue = row.getValue(columnId);

        let itemRank = undefined;
        switch (columnDef.meta?.dataType) {
            case 'text':
                itemRank = rankItem(rawValue, filterValue, { threshold: rankings.CONTAINS });
                break;
            case 'number':
                itemRank = rankItem(rawValue, filterValue, { threshold: rankings.CONTAINS });
                break;
            case 'enum':
                if (columnDef.meta?.filterConfig?.dataType === 'enum') {
                    const additionalSearchValues = columnDef.meta.filterConfig.additionalSearchValues?.(row);
                    const optionLabel = columnDef.meta.filterConfig.getOptionLabel(rawValue);
                    itemRank = rankItem(
                        additionalSearchValues ? `${optionLabel} ${additionalSearchValues.join(' ')}` : optionLabel,
                        filterValue,
                        {
                            threshold: rankings.CONTAINS,
                        },
                    );
                }
                break;

            case 'array':
                if (!Array.isArray(rawValue)) {
                    return false;
                }

                itemRank = rankItem(
                    rawValue.map((value) => {
                        if (columnDef.meta?.filterConfig?.dataType === 'array') {
                            return columnDef.meta.filterConfig.getOptionLabel(value);
                        } else {
                            return '';
                        }
                    }),
                    filterValue,
                    {
                        threshold: rankings.CONTAINS,
                    },
                );
                break;

            case 'quantity':
                // Quantity is numeric and not meaningful for text-based fuzzy search
                break;
        }

        if (!isPresent(itemRank)) {
            return false;
        }

        row.columnFiltersMeta[columnId] = {
            itemRank,
        };

        return itemRank.passed;
    };
}

// Needs `border-collapse: separate` on the table, as `DefaultTable` sets it.
/** After TanStack's v9 `column-pinning-sticky` example, as table-core's `skills/table-features` quotes it. */
export function getCommonPinningStyles<TData extends RowData>(column: Column<TData>): React.CSSProperties {
    const isPinned = column.getIsPinned();
    const isLastStartPinnedColumn = isPinned === 'start' && column.getIsLastColumn('start');
    const isFirstEndPinnedColumn = isPinned === 'end' && column.getIsFirstColumn('end');

    return {
        boxShadow: isLastStartPinnedColumn
            ? `-2px 0 4px -4px ${colorSystem.neutral[6]} inset`
            : isFirstEndPinnedColumn
              ? `2px 0 4px -4px ${colorSystem.neutral[6]} inset`
              : undefined,
        insetInlineStart: isPinned === 'start' ? `${column.getStart('start')}px` : undefined,
        insetInlineEnd: isPinned === 'end' ? `${column.getAfter('end')}px` : undefined,
        opacity: 1,
        position: isPinned ? 'sticky' : 'relative',
        width: column.getSize(),
        zIndex: isPinned ? 1 : 0,
        backgroundColor: isPinned ? colorSystem.neutral.white : undefined,
    };
}

function narrowFilterValue(unSafeFilterValue: unknown): FilterValue<unknown> | null {
    if (!isPresent(unSafeFilterValue)) {
        throwErrorUnlessProduction(`Filter value is not present`);
        return null;
    }

    if (typeof unSafeFilterValue !== 'object' || unSafeFilterValue === null) {
        throwErrorUnlessProduction(`Filter value is not an object`);
        return null;
    }

    if (!('filterFn' in unSafeFilterValue) || !('value' in unSafeFilterValue)) {
        throwErrorUnlessProduction(`Filter value is missing filterFn or value`);
        return null;
    }

    switch ((unSafeFilterValue as FilterValue<unknown>).filterFn) {
        case SupportedFilterFn.includesString:
            return { filterFn: SupportedFilterFn.includesString, value: String(unSafeFilterValue.value) };
        case SupportedFilterFn.isEmptyString:
            return { filterFn: SupportedFilterFn.isEmptyString, value: '' };
        case SupportedFilterFn.isNotEmptyString:
            return { filterFn: SupportedFilterFn.isNotEmptyString, value: '' };
        case SupportedFilterFn.inNumberRange:
            return { filterFn: SupportedFilterFn.inNumberRange, value: unSafeFilterValue.value as NumberRange };
        case SupportedFilterFn.inMonetaryValueRange:
            return { filterFn: SupportedFilterFn.inMonetaryValueRange, value: unSafeFilterValue.value as NumberRange };
        case SupportedFilterFn.inDateRange:
            return { filterFn: SupportedFilterFn.inDateRange, value: unSafeFilterValue.value as DateRange };
        case SupportedFilterFn.isInLastDays:
            return {
                filterFn: SupportedFilterFn.isInLastDays,
                value: unSafeFilterValue.value as RelativeDateFilterValue,
            };
        case SupportedFilterFn.isOlderThanDays:
            return {
                filterFn: SupportedFilterFn.isOlderThanDays,
                value: unSafeFilterValue.value as RelativeDateFilterValue,
            };
        case SupportedFilterFn.equalsAny:
            return { filterFn: SupportedFilterFn.equalsAny, value: unSafeFilterValue.value as Array<unknown> };
        case SupportedFilterFn.equalsNone:
            return { filterFn: SupportedFilterFn.equalsNone, value: unSafeFilterValue.value as Array<unknown> };
        case SupportedFilterFn.arrIncludesSome:
            return { filterFn: SupportedFilterFn.arrIncludesSome, value: unSafeFilterValue.value as Array<unknown> };
        case SupportedFilterFn.arrIncludesAll:
            return { filterFn: SupportedFilterFn.arrIncludesAll, value: unSafeFilterValue.value as Array<unknown> };
        case SupportedFilterFn.arrExcludesSome:
            return { filterFn: SupportedFilterFn.arrExcludesSome, value: unSafeFilterValue.value as Array<unknown> };
        case SupportedFilterFn.inQuantityRange:
            return { filterFn: SupportedFilterFn.inQuantityRange, value: unSafeFilterValue.value as NumberRange };
        default:
            throwErrorUnlessProduction(`Unknown filter function`);
            return null;
    }
}

export const buildFilter: RegistryFilterFn = (row, columnId, unSafeFilterValue, filterMeta) => {
    const filterValue = narrowFilterValue(unSafeFilterValue);

    if (!isPresent(filterValue)) {
        return true;
    }

    switch (filterValue.filterFn) {
        case SupportedFilterFn.includesString:
            return includesString(row, columnId, filterValue.value, filterMeta);
        case SupportedFilterFn.isEmptyString:
            return isEmptyString(row, columnId, filterValue.value, filterMeta);
        case SupportedFilterFn.isNotEmptyString:
            return isNotEmptyString(row, columnId, filterValue.value, filterMeta);
        case SupportedFilterFn.inNumberRange:
            return inNumberRange(row, columnId, filterValue.value, filterMeta);
        case SupportedFilterFn.inMonetaryValueRange:
            return inMonetaryValueRangeFilter(row, columnId, filterValue.value, filterMeta);
        case SupportedFilterFn.inDateRange:
            return inDateRange(row, columnId, filterValue.value, filterMeta);
        case SupportedFilterFn.isInLastDays:
            return isInLastDaysFilter(row, columnId, filterValue.value, filterMeta);
        case SupportedFilterFn.isOlderThanDays:
            return isOlderThanDaysFilter(row, columnId, filterValue.value, filterMeta);
        case SupportedFilterFn.equalsAny:
            return equalsAnyFilter(row, columnId, filterValue.value, filterMeta);
        case SupportedFilterFn.equalsNone:
            return equalsNoneFilter(row, columnId, filterValue.value, filterMeta);
        case SupportedFilterFn.arrIncludesSome:
            return arrIncludesSome(row, columnId, filterValue.value, filterMeta);
        case SupportedFilterFn.arrIncludesAll:
            return arrIncludesAll(row, columnId, filterValue.value, filterMeta);
        case SupportedFilterFn.arrExcludesSome:
            return arrExcludesSome(row, columnId, filterValue.value, filterMeta);
        case SupportedFilterFn.inQuantityRange:
            return inQuantityRangeFilter(row, columnId, filterValue.value, filterMeta);
        default:
            throwErrorUnlessProduction(`Unknown filter function`);
            return true;
    }
};

const includesString: RegistryFilterFn = (row, columnId, filterValue) => {
    const search = filterValue.toLowerCase();
    return Boolean(row.getValue<string | null>(columnId)?.toString()?.toLowerCase()?.includes(search));
};

const isEmptyString: RegistryFilterFn = (row, columnId) => {
    const value = row.getValue<string | null | undefined>(columnId);
    return value === null || value === undefined || value.toString().trim() === '';
};

const isNotEmptyString: RegistryFilterFn = (row, columnId) => {
    const value = row.getValue<string | null | undefined>(columnId);
    return value !== null && value !== undefined && value.toString().trim() !== '';
};

const inNumberRange: RegistryFilterFn = (row, columnId, filterValue) => {
    let [min, max] = filterValue;

    const rowValue = row.getValue<number>(columnId);
    return rowValue >= (min ?? -Infinity) && rowValue <= (max ?? Infinity);
};

const inMonetaryValueRangeFilter: RegistryFilterFn = (row, columnId, filterValue) => {
    let [min, max] = filterValue;

    const rowValue = extractAmountFromMonetaryValue(row.getValue<MonetaryValue | null | undefined>(columnId));
    return rowValue >= (min ?? -Infinity) && rowValue <= (max ?? Infinity);
};

const inDateRange: RegistryFilterFn = (row, columnId, filterValue) => {
    const [min, max] = filterValue;
    const rowValue = row.getValue<string | null | undefined>(columnId);

    const parseDate = (dateString: string | null | undefined, option: { ifAbsent: number }): number => {
        const d = new Date(Date.parse(dateString ?? ''));
        if (isNaN(d.getTime())) {
            return option.ifAbsent;
        }
        return d.getTime();
    };

    const rowDate = parseDate(rowValue, { ifAbsent: NaN });
    const minDate = parseDate(min, { ifAbsent: -Infinity });
    const maxDate = parseDate(max, { ifAbsent: Infinity });

    return rowDate >= minDate && rowDate <= maxDate;
};
export function computeRelativeDateThreshold(days: number): number {
    return Date.now() - days * 86400 * 1000;
}

function parseRowDate(row: any, columnId: string): number | null {
    const rowValue = row.getValue(columnId);
    if (!isPresent(rowValue)) {
        return null;
    }
    const rowDate = new Date(Date.parse(rowValue));
    if (isNaN(rowDate.getTime())) {
        return null;
    }
    return rowDate.getTime();
}

const isInLastDaysFilter: RegistryFilterFn = (row, columnId, filterValue) => {
    const days = filterValue as RelativeDateFilterValue;
    if (days === null) {
        return true;
    }
    const rowTime = parseRowDate(row, columnId);
    if (rowTime === null) {
        return false;
    }
    return rowTime >= computeRelativeDateThreshold(days);
};

const isOlderThanDaysFilter: RegistryFilterFn = (row, columnId, filterValue) => {
    const days = filterValue as RelativeDateFilterValue;
    if (days === null) {
        return true;
    }
    const rowTime = parseRowDate(row, columnId);
    if (rowTime === null) {
        return false;
    }
    return rowTime <= computeRelativeDateThreshold(days);
};

const equalsAnyFilter: RegistryFilterFn = (row, columnId, filterValue) => {
    return filterValue.some((val: any) => isEqual(val, row.getValue(columnId)));
};

const equalsNoneFilter: RegistryFilterFn = (row, columnId, filterValue) => {
    return filterValue.every((val: any) => !isEqual(val, row.getValue(columnId)));
};

const arrIncludesSome: RegistryFilterFn = (row, columnId: string, filterValue: unknown[]) => {
    return filterValue.some((val) => row.getValue<unknown[]>(columnId)?.some((item) => isEqual(item, val)));
};

const arrIncludesAll: RegistryFilterFn = (row, columnId: string, filterValue: unknown[]) => {
    return filterValue.every((val) => row.getValue<unknown[]>(columnId)?.some((item) => isEqual(item, val)));
};

const arrExcludesSome: RegistryFilterFn = (row, columnId: string, filterValue: unknown[]) => {
    return filterValue.every((val) => !row.getValue<unknown[]>(columnId)?.some((item) => isEqual(item, val)));
};

export const dateSortFn: RegistrySortFn = (rowA, rowB, columnId) => {
    return compareByDate((value) => String(value), { type: 'ascending', invalidDatesPlacement: 'always-end' })(
        rowA.getValue<string | null | undefined>(columnId),
        rowB.getValue<string | null | undefined>(columnId),
    );
};

export const monetaryValueSortFn: RegistrySortFn = (rowA, rowB, columnId) => {
    return compareByNumber(extractAmountFromMonetaryValue)(
        rowA.getValue<MonetaryValue | null | undefined>(columnId),
        rowB.getValue<MonetaryValue | null | undefined>(columnId),
    );
};

function extractNormalizedQuantityValue(qty: Quantity | null | undefined): number {
    if (!qty) return -Infinity;
    return QuantityHelper.from(qty).normalize().parseValue();
}

const inQuantityRangeFilter: RegistryFilterFn = (row, columnId, filterValue) => {
    const [min, max] = filterValue;
    const rowValue = extractNormalizedQuantityValue(row.getValue(columnId));
    return rowValue >= (min ?? -Infinity) && rowValue <= (max ?? Infinity);
};

export const quantitySortFn: RegistrySortFn = (rowA, rowB, columnId) => {
    return compareByNumber(extractNormalizedQuantityValue)(
        rowA.getValue<Quantity | null | undefined>(columnId),
        rowB.getValue<Quantity | null | undefined>(columnId),
    );
};

export const createPositionBasedSortFunction = <T>({
    options,
    getOptionLabel,
}: {
    options: T[] | undefined | Function;
    getOptionLabel: (opt: T) => string;
}): SortFn<any> => {
    if (Array.isArray(options)) {
        return (rowA, rowB, columnId) => {
            const a = rowA.getValue<T>(columnId);
            const b = rowB.getValue<T>(columnId);

            return options.indexOf(a) - options.indexOf(b);
        };
    } else {
        return (rowA, rowB, columnId) => {
            const a = rowA.getValue<T>(columnId);
            const b = rowB.getValue<T>(columnId);

            return compareByString(getOptionLabel(a), getOptionLabel(b));
        };
    }
};

export function resetTable<TData extends RowData>(table: Table<TData>): void {
    logToExternalErrorHandlers(new Error('Resetting table was triggered'), {
        extra: {
            table: table.options.meta?.columnsKey,
        },
    });

    const {
        columnFilters = [],
        columnOrder = [],
        columnPinning = { start: [], end: [] },
        columnVisibility = {},
        columnSizing = {},
        globalFilter = '',
        rowSelection = {},
        sorting = [],
    } = table.options.meta?.defaultInitialState ?? {};

    table.setColumnFilters(columnFilters);
    table.setColumnOrder(columnOrder);
    table.setColumnPinning(columnPinning);
    table.setColumnVisibility(columnVisibility);
    table.setColumnSizing(columnSizing);
    table.setGlobalFilter(globalFilter);
    table.setRowSelection(rowSelection);
    table.setSorting(sorting);

    table.options.meta?.setColumnRenderOptions?.({});

    Object.keys(sessionStorage).forEach((key) => {
        const { enablePersistentRowSelection, enableSaveAsDefault } = table.options.meta ?? {};

        if (isPresent(table.options.meta) && key.includes(table.options.meta.columnsKey)) {
            sessionStorage.removeItem(key);
        }

        if (enablePersistentRowSelection && key.includes(enablePersistentRowSelection)) {
            sessionStorage.removeItem(key);
        }

        if (enableSaveAsDefault && key.includes(getSaveAsDefaultConfig(enableSaveAsDefault).key)) {
            sessionStorage.removeItem(key);
        }
    });

    Object.keys(localStorage).forEach((key) => {
        const { enablePersistentRowSelection, enableSaveAsDefault } = table.options.meta ?? {};

        if (isPresent(table.options.meta) && key.includes(table.options.meta.columnsKey)) {
            localStorage.removeItem(key);
        }

        if (enablePersistentRowSelection && key.includes(enablePersistentRowSelection)) {
            localStorage.removeItem(key);
        }

        if (enableSaveAsDefault && key.includes(getSaveAsDefaultConfig(enableSaveAsDefault).key)) {
            localStorage.removeItem(key);
        }
    });
}

export function getSaveAsDefaultConfig(enableSaveAsDefault: UseTanStackTableStateProps<any>['enableSaveAsDefault']): {
    storage: Storage;
    key: string;
    enabled: boolean;
} {
    if (typeof enableSaveAsDefault === 'object' && enableSaveAsDefault !== null) {
        return {
            storage: enableSaveAsDefault.storage === 'local' ? localStorage : sessionStorage,
            key: enableSaveAsDefault.key,
            enabled: true,
        };
    } else if (typeof enableSaveAsDefault === 'string') {
        return {
            storage: sessionStorage,
            key: enableSaveAsDefault,
            enabled: true,
        };
    } else {
        return {
            storage: sessionStorage,
            key: '',
            enabled: false,
        };
    }
}
